import { showErrorMessage, showSuccessMessage } from "../../../components/global/Message/Message";
import { useState, useEffect, useRef } from "react";
import ContentViewer from "./component/ContentViewer";
import { Button, Tooltip } from "antd";
import ContentSidebarPanel from "./component/ContentSidebarPanel";
import { useContentViewer } from "../../../hooks/UserHooks/MyCoursesHook/useContentViewer";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchCourseContentByCourseId } from "../../../service/AdminService/courseService";

const ContentShowPage = ({
  selectedRecord: initialRecordProp,
  setSelectedRecord: setSelectedRecordProp,
  items: itemsProp,
  setShowContent,
  courseId: courseIdProp,
  loadCourseContent,
  quizAvailable,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId: courseIdParam } = useParams();
  const initialRecordResolved = initialRecordProp ?? location.state?.selectedRecord;
  const stateFromStorage = (() => {
    try {
      const stored = sessionStorage.getItem("contentShowState");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();
  const userRole = useSelector((state) => state.auth?.roles?.[0]);
  const currentPortal = useSelector((state) => state.auth?.currentPortal);
  const stateSource = location.state || stateFromStorage || {};
  const courseIdResolved = courseIdProp ?? stateSource.courseId ?? courseIdParam;
  const userIdResolved = stateSource.userId ?? null;

  const [selectedRecord, setSelectedRecordLocal] = useState(initialRecordResolved);
  const [allSidebarItems, setAllSidebarItems] = useState(
    itemsProp ?? location.state?.items ?? []
  );
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const fetchedAll = useRef(false);

  const [showQuizOverlay, setShowQuizOverlay] = useState(false);
  const [countdown, setCountdown] = useState(5);

  const getNextRecord = () => {
    if (!allSidebarItems || allSidebarItems.length === 0) return null;

    const currentIndex = allSidebarItems.findIndex((item) => {
      if (item === selectedRecord) return true;

      const itemId = item.courseContentId ?? item.contentId ?? item.id;
      const selectedId =
        selectedRecord?.courseContentId ?? selectedRecord?.contentId ?? selectedRecord?.id;

      return itemId && selectedId && itemId === selectedId;
    });

    if (currentIndex > 0) {
      return allSidebarItems[currentIndex - 1];
    }

    return null;
  };


  const handleNextContent = () => {
    const nextRecord = getNextRecord();
    loadCourseContent();

    if (nextRecord) {
      setSelectedRecord(nextRecord);
      const resourceType =
        nextRecord.resourceType?.toLowerCase() ||
        (nextRecord.resourceLink?.endsWith(".pdf") ? "pdf" : "video");
      setViewerType(resourceType);
      setViewerSrc(nextRecord.resourceLink);
    } else {
      showSuccessMessage("Congratulations! You have completed all course contents.");
      if (quizAvailable) {
        setShowQuizOverlay(true);
      } else {
        safeSetShowContent();
      }
    }
  };

  useEffect(() => {
    if (!courseIdResolved || fetchedAll.current) return;

    const loadAll = async () => {
      setSidebarLoading(true);
      try {
        const first = await fetchCourseContentByCourseId(courseIdResolved, {
          page: 0,
          size: 1,
          active: true,
          search: "",
        });
        const total = first?.data?.data?.totalElements ?? 0;
        if (total === 0) {
          setSidebarLoading(false);
          return;
        }

        const all = await fetchCourseContentByCourseId(courseIdResolved, {
          page: 0,
          size: total,
          active: true,
          search: "",
        });

        if (all?.data?.status === "SUCCESS") {
          const fetchedContent = all.data?.data?.content ?? [];
          const initialItems = itemsProp ?? location.state?.items ?? [];
          const mergedContent = fetchedContent.map((item) => {
            const match = initialItems.find(
              (init) =>
                (init.contentId ?? init.courseContentId) ===
                (item.contentId ?? item.courseContentId)
            );
            return {
              ...item,
              acknowledged: match?.acknowledged ?? item.acknowledged ?? false,
              completionPercentage: match?.completionPercentage ?? item.completionPercentage ?? 0,
            };
          });
          setAllSidebarItems(mergedContent);
          fetchedAll.current = true;
        }
      } catch (err) {
        console.warn("Could not load all sidebar content:", err);
      } finally {
        setSidebarLoading(false);
      }
    };

    loadAll();
  }, [courseIdResolved, itemsProp]);

  const setSelectedRecord = (val) => {
    const newVal = typeof val === "function" ? val(selectedRecord) : val;
    setSelectedRecordLocal(newVal);
    if (setSelectedRecordProp) setSelectedRecordProp(newVal);
  };

  const safeSetShowContent = setShowContent ?? (() => navigate(-1));
  const safeLoadCourseContent = loadCourseContent ?? (() => {});

  const markSidebarItemAcknowledged = (contentId) => {
    setAllSidebarItems((prev) =>
      prev.map((item) => {
        const itemId = item.courseContentId ?? item.contentId ?? item.id;
        return itemId === contentId ? { ...item, acknowledged: true } : item;
      })
    );
  };

  const {
    viewerType,
    viewerSrc,
    viewerExtra,
    visible,
    isContentCompleted,
    showToast,
    toastMessage,
    toastType,
    isMobile,
    userId,
    videoModalRef,
    pdfModalRef,
    handleSidebarChange,
    closeViewer,
    closeContentViewer,
    toggleSidebar,
    handleMarkAsDone,
    renderActionButton,
    setViewerType,
    setViewerSrc,
    setViewerExtra,
  } = useContentViewer({
    selectedRecord,
    setSelectedRecord,
    courseId: courseIdResolved,
    loadCourseContent: safeLoadCourseContent,
    setShowContent: safeSetShowContent,
    handleNextContent,
    markSidebarItemAcknowledged,
  });

    useEffect(() => {
    let timer;
    if (showQuizOverlay && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (showQuizOverlay && countdown === 0) {
      navigate(`/quiz/${courseIdResolved}?userId=${userId ?? userIdResolved}`);
    }

    return () => clearTimeout(timer);
  }, [showQuizOverlay, countdown, courseIdResolved, userId, userIdResolved, navigate]);

  const onPlayVideo = (src, type) => {
    setViewerType("video");
    setViewerSrc(src);
    setViewerExtra(type);
  };
  const onViewPDF = (src) => {
    setViewerType("pdf");
    setViewerSrc(src);
  };

  useEffect(() => {
    return () => sessionStorage.removeItem("contentShowState");
  }, []);

  useEffect(() => {
    if (!showToast) return;
    if (toastType === "success") showSuccessMessage(toastMessage);
    else showErrorMessage(toastMessage);
  }, [showToast, toastMessage, toastType]);

  return (
    <div
      className="flex flex-col bg-white overflow-hidden relative"
      style={{ height: "calc(100vh - 95px)" }}
    >
      {showQuizOverlay && (
        <div className="absolute inset-0 bg-black/75 z-50 flex flex-col items-center justify-center text-white backdrop-blur-sm transition-all duration-300">
          <div className="bg-white text-gray-800 p-8 rounded-2xl shadow-2xl flex flex-col items-center max-w-md mx-4 text-center">
            <div className="w-16 h-16 bg-[#910E11]/10 text-[#910E11] rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Course Completed!
            </h2>
            <p className="text-gray-600 mb-6">
              Great job! You will be redirected to the course quiz in...
            </p>
            <div className="text-5xl font-extrabold text-[#910E11] mb-6 animate-pulse">
              {countdown}
            </div>
            <Button
              type="primary"
              style={{ backgroundColor: "#910E11", borderColor: "#910E11" }}
              className="rounded-lg h-10 px-6"
              onClick={() =>
                navigate(`/quiz/${courseIdResolved}?userId=${userId ?? userIdResolved}`)
              }
            >
              Take Quiz Now
            </Button>
          </div>
        </div>
      )}

      {/* --- HEADER --- */}
      <div className="flex shrink-0 border-b border-gray-200" style={{ minHeight: "52px" }}>
        <div className="flex items-center justify-between flex-1 pe-6 py-3 bg-white">
          <div className="flex items-center gap-3">
            <span style={{ fontSize: "22px", fontWeight: 500, color: "#910E11" }}>
              {selectedRecord?.title || "Course Content"}
            </span>
          </div>

          {(() => {
            const isAuthorizedUser =
              userRole === "EMPLOYEE" ||
              (userRole === "ADMIN_SUPPORT" && currentPortal === "user");

            const shouldShowButton =
              isAuthorizedUser &&
              (viewerType === "video" || (viewerType === "pdf" && isContentCompleted));

            if (!shouldShowButton) return null;

            return (
              <Tooltip title={isContentCompleted ? "Already marked as completed" : "Mark as Done"}>
                <Button
                  onClick={handleMarkAsDone}
                  disabled={isContentCompleted}
                  style={
                    isContentCompleted
                      ? {
                          backgroundColor: "#088A15",
                          borderColor: "#088A15",
                          color: "#fff",
                          fontWeight: 500,
                          borderRadius: "8px",
                          cursor: "default",
                        }
                      : {
                          borderColor: "#910E11",
                          color: "#910E11",
                          fontWeight: 500,
                          backgroundColor: "transparent",
                          borderRadius: "8px",
                        }
                  }
                >
                  {isContentCompleted ? "Completed" : "Mark as done"}
                </Button>
              </Tooltip>
            );
          })()}
        </div>

        {visible ? (
          <div
            className="shrink-0 flex items-center justify-between px-5 border-l border-[#e2e8f0]"
            style={{ width: "280px", backgroundColor: "#F3F4F5" }}
          >
            <span style={{ color: "#910E11", fontWeight: 600, fontSize: "15px" }}>
              Course Content
            </span>
            <span
              onClick={toggleSidebar}
              style={{
                color: "#910E11",
                fontSize: "20px",
                cursor: "pointer",
                fontWeight: 600,
                padding: "4px 0 4px 8px",
              }}
            >
              ›
            </span>
          </div>
        ) : (
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            onClick={toggleSidebar}
          >
            <div
              style={{
                backgroundColor: "#910E11",
                width: "52px",
                height: "52px",
                borderRadius: "10px 0 0 10px",
                display: "flex",
                alignItems: "center",
                justify: "center",
                boxShadow: "-2px 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              <div
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "50%",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                  justify: "center",
                }}
              >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                  <path
                    d="M6 2L2 7L6 12"
                    stroke="#910E11"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* --- CONTENT & SIDEBAR --- */}
      <div className="flex flex-1 overflow-hidden relative bg-black min-h-0">
        <ContentViewer
          viewerType={viewerType}
          viewerSrc={viewerSrc}
          viewerExtra={viewerExtra}
          selectedRecord={selectedRecord}
          courseId={courseIdResolved}
          userId={userId ?? userIdResolved}
          videoModalRef={videoModalRef}
          pdfModalRef={pdfModalRef}
          closeViewer={closeViewer}
          closeContentViewer={closeContentViewer}
          sidebarVisible={visible}
          isMobile={isMobile}
          onNextContent={handleNextContent}
          isContentCompleted={isContentCompleted}
          markSidebarItemAcknowledged={markSidebarItemAcknowledged}
        />

        {visible && (
          <div
            className="flex flex-col shrink-0 border-l border-[#e2e8f0] overflow-hidden"
            style={{ width: "280px", backgroundColor: "#F9F9F9" }}
          >
            <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-[3px]">
              <ContentSidebarPanel
                items={allSidebarItems}
                sidebarLoading={sidebarLoading}
                onPlayVideo={onPlayVideo}
                onViewPDF={onViewPDF}
                visible={true}
                setViewerSrc={setViewerSrc}
                setViewerExtra={setViewerExtra}
                setViewerType={setViewerType}
                setSelectedRecord={setSelectedRecord}
                selectedRecord={selectedRecord}
                renderActionButton={renderActionButton}
                onChange={handleSidebarChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentShowPage;